import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

function Tile({ count, label, pills = [], onClick, span }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)',
        borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
        gridColumn: span ? `span ${span}` : undefined, transition: 'border-color 0.15s'
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--acc)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-md)'}
    >
      <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 }}>{count}</div>
      <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {pills.map((p, i) => (
          <span key={i} style={{ display: 'inline-flex', padding: '2px 7px', background: 'var(--bg-secondary)', border: '0.5px solid var(--border)', borderRadius: 20, fontSize: 10, color: 'var(--text-secondary)' }}>
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}

// Builds display pills from real data: shows all names if there are only a
// few, otherwise the first couple plus a "+N" count for the rest.
function makePills(names, maxShow = 2) {
  const clean = names.filter(Boolean);
  if (clean.length <= maxShow + 1) return clean;
  return [...clean.slice(0, maxShow), `+${clean.length - maxShow}`];
}

export default function MastersHome() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    divisions: 0, factories: 0, lines: 0, operations: 0, groups: 0, machines: 0,
    employees: { total: 0, operators: 0, helpers: 0, skilled: 0, developing: 0, training: 0, avg_ops: 0 },
    pills: { divisions: [], factories: [], lines: [], groups: [], machines: [] }
  });

  useEffect(() => {
    document.getElementById('lc-page-title').textContent = 'Masters home';
    Promise.allSettled([
      api.get('/divisions'),
      api.get('/factories'),
      api.get('/shift-lines'),
      api.get('/operations'),
      api.get('/machine-types'),
      api.get('/employees'),
      api.get('/fundamental-groups'),
    ]).then(([divs, facts, lines, ops, mach, emps, groups]) => {
      const employees = emps.value?.data || [];
      const operators = employees.filter(e => e.emp_type === 'Operator');
      const helpers = employees.filter(e => e.emp_type === 'Helper');
      const skilled = employees.filter(e => {
        const avg = e.skills?.length ? e.skills.reduce((s, sk) => s + parseFloat(sk.skill_pct || 0), 0) / e.skills.length : 0;
        return avg >= 80;
      }).length;
      const developing = employees.filter(e => {
        const avg = e.skills?.length ? e.skills.reduce((s, sk) => s + parseFloat(sk.skill_pct || 0), 0) / e.skills.length : 0;
        return avg >= 60 && avg < 80;
      }).length;
      const training = employees.filter(e => {
        const avg = e.skills?.length ? e.skills.reduce((s, sk) => s + parseFloat(sk.skill_pct || 0), 0) / e.skills.length : 0;
        return avg > 0 && avg < 60;
      }).length;
      const avg_ops = employees.length ? (employees.reduce((s, e) => s + (e.skills?.length || 0), 0) / employees.length).toFixed(1) : 0;

      const divisionsData = divs.value?.data || [];
      const factoriesData = facts.value?.data || [];
      const linesData = lines.value?.data || [];
      const groupsData = groups.value?.data || [];
      const machinesData = mach.value?.data || [];

      setStats({
        divisions: divisionsData.length,
        factories: factoriesData.length,
        lines: linesData.length,
        operations: ops.value?.data?.length || 0,
        groups: groupsData.length,
        machines: machinesData.reduce((s, m) => s + m.count_in_unit, 0) || 0,
        employees: { total: employees.length, operators: operators.length, helpers: helpers.length, skilled, developing, training, avg_ops },
        pills: {
          divisions: makePills(divisionsData.map(d => d.name)),
          factories: makePills(factoriesData.map(f => f.name)),
          lines: makePills([...new Set(linesData.map(l => l.shift_name))]),
          groups: makePills(groupsData.map(g => g.name)),
          machines: makePills([...new Set(machinesData.map(m => m.machine_type))]),
        }
      });
    });
  }, []);

  return (
    <div style={{ padding: 18 }}>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 2 }}>Masters</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>Click any tile to manage records</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 10 }}>
        <Tile count={stats.divisions} label="Divisions" pills={stats.pills.divisions} onClick={() => navigate('/masters/divisions')} />
        <Tile count={stats.factories} label="Factories" pills={stats.pills.factories} onClick={() => navigate('/masters/factories')} />
        <Tile count={stats.lines} label="Shift-lines" pills={stats.pills.lines} onClick={() => navigate('/masters/shift-lines')} />
        <Tile
          count={stats.operations} label="Operations"
          pills={[]}
          onClick={() => navigate('/masters/operations')}
        >
          <span className="bd bd-blue" style={{ fontSize: 9.5 }}>Machine: {Math.floor(stats.operations * 0.65)}</span>
          <span className="bd bd-gray" style={{ fontSize: 9.5, marginLeft: 4 }}>Non: {Math.ceil(stats.operations * 0.35)}</span>
        </Tile>
        <Tile count={stats.groups} label="Fund. operation groups" pills={stats.pills.groups} onClick={() => navigate('/masters/fund-groups')} />
        <Tile count={stats.machines} label="Machines" pills={stats.pills.machines} onClick={() => navigate('/masters/machines')} />
      </div>

      {/* Employee tile — full width */}
      <div
        style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', transition: 'border-color 0.15s' }}
        onClick={() => navigate('/masters/employees')}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--acc)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-md)'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 500 }}>{stats.employees.total}</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginBottom: 8 }}>Employees</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span className="bd bd-blue" style={{ fontSize: 9.5 }}>Operators: {stats.employees.operators}</span>
              <span className="bd bd-gray" style={{ fontSize: 9.5 }}>Helpers: {stats.employees.helpers}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, flex: 2 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 500, color: 'var(--grn)' }}>{stats.employees.skilled}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Skill ≥ 80%</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 500, color: 'var(--amb)' }}>{stats.employees.developing}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Skill 60–79%</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 500, color: 'var(--red)' }}>{stats.employees.training}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Skill &lt; 60%</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 500 }}>{stats.employees.avg_ops}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Avg ops/emp</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}